/**
 * AddPartnerModal — Modal for adding a project partner.
 *
 * Two modes:
 *   1. Entity Search — Typeahead picker searching L4E community entities
 *      (follows CommunityPicker scored-matching pattern).
 *   2. Manual Entry — Name, optional logo upload, website URL, partner role.
 *
 * The DB constraint `chk_partner_source` requires at least one of
 * entityGuid or manualName. The modal enforces this before submitting.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { normalizeGuid } from '../../utils/guid';
import { pathFromLeafGuid, pathDisplay } from '../../utils/entityPath';
import { communityMembershipStore } from '../../Data/store';
import { getCurrentUserGuid } from '../../api/client';
import { compressImage } from '../../lib/compressImage';
import { uploadFile } from '../../api/fileStorage';
import type { CreateProjectPartnerRequest } from '../../api/projectPartners';
import styles from './AddPartnerModal.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface AddPartnerModalProps {
  projectGuid: string;
  /** Community entities for typeahead search. */
  entities: Array<Record<string, any>>;
  /** Relationships for building path display. */
  relationships: Array<Record<string, any>>;
  /** Called with the request body when user confirms. */
  onSubmit: (body: CreateProjectPartnerRequest) => Promise<void>;
  /** Called when the user dismisses the modal. */
  onClose: () => void;
  /** When true, only show the entity search (no manual entry). Used for linking. */
  entityOnly?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Partner role options (match i18n keys)                              */
/* ------------------------------------------------------------------ */

const ROLE_OPTIONS = [
  'sponsor',
  'partner',
  'collaborator',
  'funder',
  'supporter',
  'advisor',
] as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AddPartnerModal({
  projectGuid,
  entities,
  relationships,
  onSubmit,
  onClose,
  entityOnly,
}: AddPartnerModalProps) {
  const { t } = useAppTranslation('pages');

  // ── State ──────────────────────────────────────────────
  const [mode, setMode] = useState<'search' | 'manual'>('search');

  // Entity search state
  const [query, setQuery] = useState('');
  const [selectedGuid, setSelectedGuid] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Manual entry state
  const [manualName, setManualName] = useState('');
  const [manualWebsite, setManualWebsite] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploading, setUploading] = useState(false);

  // Common
  const [role, setRole] = useState('partner');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const searchRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Membership set for boost ───────────────────────────
  const currentUserGuid = getCurrentUserGuid();
  const memberGuids = useMemo(() => {
    const upperUser = currentUserGuid.toUpperCase();
    const set = new Set<string>();
    for (const m of communityMembershipStore) {
      if (
        String(m.user_guid || '').toUpperCase() === upperUser &&
        String(m.status || '').toLowerCase() === 'active'
      ) {
        set.add(String(m.community_entity_guid || '').toUpperCase());
      }
    }
    return set;
  }, [currentUserGuid]);

  // ── Path display helper ────────────────────────────────
  const getPathDisplay = useCallback(
    (leafGuid: string): string => {
      const g = normalizeGuid(leafGuid);
      if (!g) return '';
      const rootToLeaf = pathFromLeafGuid(g, entities as any, relationships as any);
      const parts = (rootToLeaf ?? [])
        .map((p: any) => String(p?.name ?? '').trim())
        .filter(Boolean);
      return pathDisplay(parts.reverse());
    },
    [entities, relationships]
  );

  // ── Search index ───────────────────────────────────────
  const entityIndex = useMemo(() => {
    const out: {
      guid: string;
      display: string;
      search: string;
      leafName: string;
      isMember: boolean;
      entityType: string;
    }[] = [];
    if (!Array.isArray(entities)) return out;
    for (const e of entities) {
      const guid = normalizeGuid(e?.entity_guid || e?.entityGuid);
      if (!guid) continue;
      const isActive = e?.is_active ?? e?.isActive ?? true;
      if (!isActive) continue;
      const leafName = String(e?.name ?? '').trim();
      const display = getPathDisplay(guid);
      if (!display) continue;
      const search = `${leafName} ${display}`.toLowerCase();
      const isMember = memberGuids.has(guid.toUpperCase());
      const entityType = String(e?.entity_type || e?.entityType || '');
      out.push({ guid, display, search, leafName, isMember, entityType });
    }
    out.sort((a, b) => {
      if (a.isMember !== b.isMember) return a.isMember ? -1 : 1;
      return a.display.localeCompare(b.display);
    });
    return out;
  }, [entities, getPathDisplay, memberGuids]);

  // ── Scored suggestions ─────────────────────────────────
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as typeof entityIndex;

    const scored: (typeof entityIndex[number] & { score: number })[] = [];
    for (const row of entityIndex) {
      const name = row.leafName.toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (row.search.startsWith(q)) score = 90;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;
      if (score < 0) continue;
      const depthBoost = Math.min(20, row.display.split(',').length);
      const memberBoost = row.isMember ? 10 : 0;
      scored.push({ ...row, score: score + depthBoost + memberBoost });
    }
    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12);
  }, [query, entityIndex]);

  // ── Close dropdown on outside click ────────────────────
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  // ── Esc to close modal ─────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Logo file handler ──────────────────────────────────
  const handleLogoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  // ── Submit ─────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setError('');
    const body: CreateProjectPartnerRequest = {
      partnerRole: role,
    };

    if (mode === 'search') {
      if (!selectedGuid) {
        setError('Please select an organization.');
        return;
      }
      body.entityGuid = selectedGuid;
    } else {
      if (!manualName.trim()) {
        setError('Organization name is required.');
        return;
      }
      body.manualName = manualName.trim();
      if (manualWebsite.trim()) body.manualWebsiteUrl = manualWebsite.trim();

      // Upload logo if provided
      if (logoFile) {
        try {
          setUploading(true);
          const compressed = await compressImage(logoFile, {
            maxDimension: 400,
            quality: 0.85,
            maxBytes: 200_000,
          });
          const ext = compressed.blob.type === 'image/png' ? 'png' : 'jpg';
          const result = await uploadFile(
            compressed.blob,
            'logos',
            projectGuid,
            `partner-logo-${Date.now()}.${ext}`
          );
          body.manualLogoUrl = result.url;
        } catch (err) {
          console.error('[AddPartnerModal] logo upload failed', err);
          // Continue without logo — not a blocking error
        } finally {
          setUploading(false);
        }
      }
    }

    try {
      setSaving(true);
      await onSubmit(body);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add partner');
    } finally {
      setSaving(false);
    }
  }, [mode, selectedGuid, manualName, manualWebsite, logoFile, role, projectGuid, onSubmit, onClose]);

  // ── Derived ────────────────────────────────────────────
  const canSubmit =
    mode === 'search' ? !!selectedGuid : !!manualName.trim();
  const busy = saving || uploading;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            {t('projectDetail.about.addPartner')}
          </h3>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Mode tabs (hidden in entityOnly/link mode) */}
        {!entityOnly && (
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${mode === 'search' ? styles.tabActive : ''}`}
              onClick={() => setMode('search')}
            >
              {t('projectDetail.about.entitySearch').replace('...', '')}
            </button>
            <button
              className={`${styles.tab} ${mode === 'manual' ? styles.tabActive : ''}`}
              onClick={() => setMode('manual')}
            >
              {t('projectDetail.about.orEnterManually')}
            </button>
          </div>
        )}

        {/* Body */}
        <div className={styles.body}>
          {/* ── Entity search mode ── */}
          {mode === 'search' && (
            <div ref={searchRef} style={{ position: 'relative' }}>
              {/* Selected token */}
              {selectedGuid ? (
                <div className={styles.token}>
                  <span className={styles.tokenText}>{selectedName}</span>
                  <button
                    className={styles.tokenClear}
                    onClick={() => {
                      setSelectedGuid('');
                      setSelectedName('');
                      setQuery('');
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <input
                  className={styles.input}
                  value={query}
                  placeholder={t('projectDetail.about.entitySearch')}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  autoFocus
                />
              )}

              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className={styles.suggestions}>
                  {suggestions.map((s) => (
                    <div
                      key={s.guid}
                      className={styles.suggestionItem}
                      onMouseDown={() => {
                        setSelectedGuid(s.guid);
                        setSelectedName(s.display);
                        setQuery('');
                        setShowSuggestions(false);
                      }}
                    >
                      <span className={styles.suggestionPath}>{s.display}</span>
                      {s.isMember && (
                        <span className={styles.memberBadge}>Member</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Manual entry mode ── */}
          {mode === 'manual' && (
            <div className={styles.manualForm}>
              <label className={styles.label}>
                {t('projectDetail.about.manualName')}
                <input
                  className={styles.input}
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  autoFocus
                />
              </label>

              <label className={styles.label}>
                {t('projectDetail.about.manualWebsiteUrl')}
                <input
                  className={styles.input}
                  value={manualWebsite}
                  onChange={(e) => setManualWebsite(e.target.value)}
                  placeholder="https://..."
                />
              </label>

              {/* Logo upload */}
              <div className={styles.logoRow}>
                <span className={styles.label}>Logo</span>
                <div className={styles.logoControls}>
                  {logoPreview ? (
                    <div className={styles.logoPreview}>
                      <img src={logoPreview} alt="Logo preview" />
                      <button
                        className={styles.logoRemove}
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview('');
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      className={styles.logoUploadBtn}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleLogoChange}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Partner role (shared) ── */}
          <label className={styles.label} style={{ marginBlockStart: 16 }}>
            Role
            <select
              className={styles.select}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`projectDetail.about.partnerRoles.${r}`)}
                </option>
              ))}
            </select>
          </label>

          {/* Error */}
          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!canSubmit || busy}
          >
            {busy ? t('projectDetail.about.saving') : t('projectDetail.about.addPartner')}
          </button>
        </div>
      </div>
    </div>
  );
}
